import { useCallback } from 'react';
import { useBi } from '@components/Hooks/useBi';
import { anyButtonClick } from '@wix/bi-logger-seo-data/v2';
import { marketplaceHeaderClientGenericClick } from '@wix/bi-logger-partners-data/v2';
import { COMMUNITY_NAV_DROPDOWN_OPTIONS } from '../navSubHeaderUtils';
import { useRouter as useCompatRouter } from 'next/compat/router';
import { usePathname } from 'next/navigation';

export const SUBHEADER_BI_CLICK_TYPES = {
  studioTemplates: 'studio templates',
  sellInStudioTemplates: 'sell in the marketplace - sell studio templates',
} as const;

export type ISubheaderBiClickType =
  (typeof SUBHEADER_BI_CLICK_TYPES)[keyof typeof SUBHEADER_BI_CLICK_TYPES];

export const SUBHEADER_PAGE_NAMES = {
  templates: 'templates',
  template: 'template',
} as const;

export const ACTION_NAMES = {
  OpenMenu: 'OpenMenu',
  ExternalLink: 'ExternalLink',
  PageLink: 'PageLink',
} as const;

export type ISubheaderPageName =
  (typeof SUBHEADER_PAGE_NAMES)[keyof typeof SUBHEADER_PAGE_NAMES];

interface IReportElementsClickParams {
  elementTitle: string;
  actionName: string;
  value?: string;
}

interface IReportMarketplaceHeaderClickParams {
  clickType: ISubheaderBiClickType;
  clickData?: string | Record<string, unknown>;
}

function isMarketplaceHeaderClickParams(
  props: IReportElementsClickParams | IReportMarketplaceHeaderClickParams,
): props is IReportMarketplaceHeaderClickParams {
  return (
    'clickType' in props &&
    typeof (props as IReportMarketplaceHeaderClickParams).clickType === 'string'
  );
}

function isElementsClickParams(
  props: IReportElementsClickParams | IReportMarketplaceHeaderClickParams,
): props is IReportElementsClickParams {
  return 'elementTitle' in props && 'actionName' in props && 'value' in props;
}

const useCommunityNavSubHeaderBi = () => {
  const compatRouter = useCompatRouter();
  const appPathname = usePathname();
  const pathname = compatRouter?.pathname ?? appPathname ?? '';
  const pageName = pathname.includes('templates')
    ? SUBHEADER_PAGE_NAMES.templates
    : SUBHEADER_PAGE_NAMES.template;

  const currentUrl =
    typeof window !== 'undefined'
      ? new URL(window.location.href)?.toString()
      : undefined;
  const logger = useBi();

  const reportElementsClick = useCallback(
    (payload: IReportElementsClickParams) => {
      logger.report(
        anyButtonClick({
          elementTitle: payload.elementTitle,
          actionName: payload.actionName,
          value: payload.value,
          url: currentUrl,
        }),
      );
    },
    [logger, currentUrl],
  );

  const reportMarketplaceHeaderClick = useCallback(
    ({ clickType, clickData }: IReportMarketplaceHeaderClickParams) => {
      const click_data =
        typeof clickData === 'object'
          ? JSON.stringify(clickData)
          : clickData ?? undefined;

      logger.report(
        marketplaceHeaderClientGenericClick({
          clickType,
          click_data,
          ...(pageName && { pageName }),
        }),
      );
    },
    [logger, pageName],
  );

  const marketplaceHeaderClickEventKeys = [
    COMMUNITY_NAV_DROPDOWN_OPTIONS.share[0].id,
    COMMUNITY_NAV_DROPDOWN_OPTIONS.share[1].id,
  ] as const;

  const getIsMarketplaceHeaderClickEvent = (
    key: string,
  ): key is (typeof marketplaceHeaderClickEventKeys)[number] =>
    marketplaceHeaderClickEventKeys.includes(key);

  const reportSubHeaderClick = useCallback(
    (
      key: string,
      eventProps:
        | IReportElementsClickParams
        | IReportMarketplaceHeaderClickParams,
    ) => {
      if (
        getIsMarketplaceHeaderClickEvent(key) &&
        isMarketplaceHeaderClickParams(eventProps)
      ) {
        reportMarketplaceHeaderClick(eventProps);
      } else if (
        !getIsMarketplaceHeaderClickEvent(key) &&
        isElementsClickParams(eventProps)
      ) {
        reportElementsClick(eventProps);
      }
    },
    [reportMarketplaceHeaderClick, reportElementsClick],
  );

  return {
    reportElementsClick,
    reportMarketplaceHeaderClick,
    getIsMarketplaceHeaderClickEvent,
    reportSubHeaderClick,
  };
};

export default useCommunityNavSubHeaderBi;
